// Types de données pour l'application de gestion d'eau potable

export const SUBSCRIBER_TYPES = {
  INDIVIDUAL: 'individual',
  PROFESSIONAL: 'professional',
  ADMINISTRATION: 'administration'
};

export const SUBSCRIBER_TYPE_LABELS = {
  individual: 'Particulier',
  professional: 'Professionnel',
  administration: 'Administration'
};

export const METER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DEFECTIVE: 'defective',
  MAINTENANCE: 'maintenance',
  REPLACED: 'replaced'
};

export const METER_STATUS_LABELS = {
  active: 'Actif',
  inactive: 'Inactif',
  defective: 'Défectueux',
  maintenance: 'Maintenance',
  replaced: 'Remplacé'
};

export const BILLING_FREQUENCY = {
  MONTHLY: 'monthly',
  BIMONTHLY: 'bimonthly',
  QUARTERLY: 'quarterly'
};

export const BILLING_FREQUENCY_LABELS = {
  monthly: 'Mensuel',
  bimonthly: 'Bimestriel',
  quarterly: 'Trimestriel'
};

export const BILLING_STATUS = {
  DRAFT: 'draft',
  ISSUED: 'issued',
  PAID: 'paid',
  PARTIALLY_PAID: 'partially_paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled'
};

export const BILLING_STATUS_LABELS = {
  draft: 'Brouillon',
  issued: 'Émise',
  paid: 'Payée',
  partially_paid: 'Partiellement payée',
  overdue: 'En retard',
  cancelled: 'Annulée'
};

export const PAYMENT_METHODS = {
  CASH: 'cash',
  CHECK: 'check',
  BANK_TRANSFER: 'bank_transfer',
  MOBILE_MONEY: 'mobile_money',
  CARD: 'card'
};

export const PAYMENT_METHOD_LABELS = {
  cash: 'Espèces',
  check: 'Chèque',
  bank_transfer: 'Virement bancaire',
  mobile_money: 'Mobile Money',
  card: 'Carte bancaire'
};

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

export const PAYMENT_STATUS_LABELS = {
  pending: 'En attente',
  completed: 'Complétée',
  failed: 'Échec',
  cancelled: 'Annulée'
};

// Modèle Subscriber
export const createSubscriber = (data = {}) => {
  const subscriber = {
    id: data.id || Date.now().toString(),
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    email: data.email || '',
    phone: data.phone || '',
    address: data.address || '',
    city: data.city || '',
    district: data.district || '',
    type: data.type || SUBSCRIBER_TYPES.INDIVIDUAL,
    registrationNumber: data.registrationNumber || ''
  };
  
  // Ajouter les colonnes optionnelles seulement si elles sont fournies
  // Cela évite les erreurs de schema cache sur les colonnes qui pourraient ne pas exister
  if (data.cin) subscriber.cin = data.cin;
  if (data.caution !== undefined) subscriber.caution = data.caution;
  if (data.cautionPaid !== undefined) subscriber.cautionPaid = data.cautionPaid;
  if (data.status) subscriber.status = data.status;
  if (data.registrationDate) subscriber.registrationDate = data.registrationDate;
  if (data.notes) subscriber.notes = data.notes;
  
  return subscriber;
};

// Modèle Meter
export const createMeter = (data = {}) => ({
  id: data.id || Date.now().toString(),
  serialNumber: data.serialNumber || '',
  brand: data.brand || '',
  model: data.model || '',
  capacity: data.capacity || 0,
  calibrationDate: data.calibrationDate || new Date().toISOString(),
  installationDate: data.installationDate || new Date().toISOString(),
  replacementDate: data.replacementDate || null,
  lifespan: data.lifespan || 10, // en années
  status: data.status || METER_STATUS.ACTIVE,
  subscriberId: data.subscriberId || '',
  initialReading: data.initialReading || 0,
  lastReading: data.lastReading || 0,
  anomalyAlert: data.anomalyAlert || false
});

// Modèle Reading (Relevé)
export const createReading = (data = {}) => ({
  id: data.id || Date.now().toString(),
  meterId: data.meterId || '',
  subscriberId: data.subscriberId || '',
  readingDate: data.readingDate || new Date().toISOString(),
  meterReading: data.meterReading || 0,
  consumption: data.consumption || 0,
  notes: data.notes || '',
  linkedInvoiceId: data.linkedInvoiceId || null, // ID de la facture générée à partir de ce relevé
  invoiceGenerated: data.invoiceGenerated || false // Flag pour indiquer si une facture a été générée
});

// Modèle Tariff
export const createTariff = (data = {}) => ({
  id: data.id || Date.now().toString(),
  subscriberType: data.subscriberType || SUBSCRIBER_TYPES.INDIVIDUAL,
  minVolume: data.minVolume || 0,
  maxVolume: data.maxVolume || 10,
  pricePerM3: data.pricePerM3 || 0,
  effectiveDate: data.effectiveDate || new Date().toISOString()
});

// Modèle Invoice/Facture
export const createInvoice = (data = {}) => ({
  id: data.id || Date.now().toString(),
  invoiceNumber: data.invoiceNumber || '',
  subscriberId: data.subscriberId || '',
  subscriberName: data.subscriberName || '',
  meterId: data.meterId || '',
  billingPeriod: data.billingPeriod || '',
  billingDate: data.billingDate || new Date().toISOString(),
  dueDate: data.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  readingStart: data.readingStart || 0,
  readingEnd: data.readingEnd || 0,
  totalConsumption: data.totalConsumption || 0,
  baseAmount: data.baseAmount || 0,
  tax: data.tax || 0,
  penalties: data.penalties || 0,
  totalAmount: data.totalAmount || 0,
  paidAmount: data.paidAmount || 0,
  remainingAmount: data.remainingAmount || 0,
  status: data.status || BILLING_STATUS.ISSUED,
  notes: data.notes || ''
});

// Modèle Payment
export const createPayment = (data = {}) => ({
  id: data.id || Date.now().toString(),
  invoiceId: data.invoiceId || '',
  subscriberId: data.subscriberId || '',
  amount: data.amount || 0,
  paymentDate: data.paymentDate || new Date().toISOString(),
  method: data.method || PAYMENT_METHODS.CASH,
  reference: data.reference || ('PAY-' + (data.id || Date.now().toString())),
  notes: data.notes || ''
});

// Modèle Maintenance
export const createMaintenance = (data = {}) => ({
  id: data.id || Date.now().toString(),
  meterId: data.meterId || '',
  date: data.date || new Date().toISOString(),
  type: data.type || 'repair', // repair, cleaning, calibration
  description: data.description || '',
  technician: data.technician || '',
  cost: data.cost || 0,
  status: data.status || 'completed'
});
